import axiosInstance from "../../axiosConfig";

// FIX: axiosInstance in this codebase has NO baseURL configured — every other
// file (EmailAutomation.jsx, LaunchedTemplates.jsx, etc.) builds the full URL
// per call via `${process.env.REACT_APP_API_BASE_URL}/...`. The previous
// version of this file assumed axiosInstance's baseURL already included
// "/api" and used bare relative paths ("/admin/scheduled-events/..."), which
// would have resolved against the page's own origin instead of the API and
// 404'd. All calls below now follow the established pattern.
//
// NOTE: paths below drop the leading "/admin" since REACT_APP_API_BASE_URL
// already resolves to ".../api/admin" elsewhere in this codebase (confirmed
// by e.g. LaunchedTemplates.jsx calling
// `${REACT_APP_API_BASE_URL}/scheduled-events/launched-notifications`
// against the documented `/api/admin/scheduled-events/launched-notifications`
// route). If your env var is set up differently, adjust the prefix here.
const BASE = process.env.REACT_APP_API_BASE_URL;

/* -------------------------------------------------------------------------
 * Scheduled Events  ->  /scheduled-events
 * ---------------------------------------------------------------------- */

export const fetchScheduledEvents = () =>
  axiosInstance.get(`${BASE}/scheduled-events/notifications`);

export const fetchScheduledEventByNotificationId = (notificationId) =>
  axiosInstance.get(`${BASE}/scheduled-events/notification/${notificationId}`);

// DELETE /scheduled-events/notification/{notificationId}
export const deleteScheduledEventNotification = (notificationId) =>
  axiosInstance.delete(
    `${BASE}/scheduled-events/notification/${notificationId}`,
  );

export const fetchScheduledEventsByCategory = (categoryId) =>
  axiosInstance.get(`${BASE}/scheduled-events/category/${categoryId}`);

export const fetchScheduledEventsByEventType = (eventTypeId) =>
  axiosInstance.get(
    `${BASE}/scheduled-events/scheduled-events/event-type/${eventTypeId}`,
  );

// Returns [{ eventId, eventName }, ...] — used to populate the event type select.
export const fetchEventTypes = () =>
  axiosInstance.get(`${BASE}/scheduled-events/event-types`);

// Confirmed: a single POST /scheduled-events endpoint handles both create
// and update (scheduledEventId 0/null = create, a real id = update) — see
// createScheduledEvent/updateScheduledEvent create/update, matching the
// documented /api/admin/scheduled-events contract.
export const createScheduledEvent = (payload) =>
  axiosInstance.post(`${BASE}/scheduled-events`, payload);

export const updateScheduledEvent = (payload) =>
  axiosInstance.put(`${BASE}/scheduled-events`, payload);

// Body: { notifications: [{ notificationId, status }, ...] }, status being
// "LAUNCHED" or "ON_HOLD" (also seen: "PENDING", the default/initial state).
export const updateScheduledEventNotificationStatus = (ids, status) =>
  axiosInstance.put(`${BASE}/scheduled-events/notifications/status`, {
    notifications: ids.map((notificationId) => ({ notificationId, status })),
  });

/* -------------------------------------------------------------------------
 * Scheduled Cron Jobs  ->  /scheduled-cron-jobs
 * ---------------------------------------------------------------------- */

export const fetchScheduledCronJobs = () =>
  axiosInstance.get(`${BASE}/scheduled-cron-jobs/automations`);

export const fetchScheduledCronJobByAutomationId = (automationId) =>
  axiosInstance.get(`${BASE}/scheduled-cron-jobs/automation/${automationId}`);

// DELETE /scheduled-cron-jobs/automation/{automationId}
export const deleteScheduledCronJobAutomation = (automationId) =>
  axiosInstance.delete(
    `${BASE}/scheduled-cron-jobs/automation/${automationId}`,
  );

export const fetchScheduledCronJobsByCategory = (categoryId) =>
  axiosInstance.get(`${BASE}/scheduled-cron-jobs/category/${categoryId}`);

// GET /scheduled-cron-jobs/notifications/cron-jobs/{categoryId}
// Returns [{ categoryId, categoryName, cronJobTypes: [{ cronJobTypeId,
// cronJobTypeName, count, scheduledCronJobs: [...] }] }]. Unlike
// fetchScheduledCronJobsByCategory (which only returns the *type* list for
// this category), this nests the actual scheduled cron job records
// (already shaped like the items from fetchScheduledCronJobs) grouped by
// type — so it can drive both the "Cron job type" dropdown AND a table
// that's genuinely filtered to the selected category, in one call. Used by
// the Automation page. Paginated "group of 10 records" per the swagger
// description; adjust here if the backend adds page params later.
export const fetchAutomationsWithScheduledCronJobsByCategory = (categoryId) =>
  axiosInstance.get(
    `${BASE}/scheduled-cron-jobs/notifications/cron-jobs/${categoryId}`,
  );

// Returns [{ cronJobId, eventName }, ...] (yes, "eventName" per your swagger,
// even though it's the cron job type's display name) — used to populate the
// cron job type select.
export const fetchCronJobTypes = () =>
  axiosInstance.get(`${BASE}/scheduled-cron-jobs/cron-job-types`);

// Confirmed: a single POST /scheduled-cron-jobs endpoint handles both
// create and update (scheduledCronJobId 0/null = create, a real id =
// update), matching the documented /api/admin/scheduled-cron-jobs contract.
export const createScheduledCronJob = (payload) =>
  axiosInstance.post(`${BASE}/scheduled-cron-jobs`, payload);

export const updateScheduledCronJob = (payload) =>
  axiosInstance.put(`${BASE}/scheduled-cron-jobs`, payload);

// Update automation launch status. The backend expects an object rather than
// a raw array, with each automation ID paired with the requested status.
export const launchScheduledCronJobAutomations = (automationIds, status) =>
  axiosInstance.put(
    `${BASE}/scheduled-cron-jobs/automations/status`,
    {
      automations: automationIds.map((automationId) => ({
        automationId,
        status,
      })),
    },
  );

/* -------------------------------------------------------------------------
 * Modules & Categories  ->  /module-plans, /automation-categories
 * ---------------------------------------------------------------------- */

// Returns [{ moduleId, moduleName, moduleAccessId, description }, ...]
export const fetchModulePlans = () =>
  axiosInstance.get(`${BASE}/module-plans/module-plans`);

// Returns [{ categoryId, categoryName }, ...] for the given module.
export const fetchAutomationCategoriesByModule = (moduleId) =>
  axiosInstance.get(`${BASE}/automation-categories/module/${moduleId}`);

/* -------------------------------------------------------------------------
 * Notification types  ->  /notification-types
 * ---------------------------------------------------------------------- */

// Returns [{ notificationTypeId, type, name }, ...] — used to populate the
// notification type multi-select in the form.
export const fetchNotificationTypes = () =>
  axiosInstance.get(`${BASE}/notification-types/all`);

export const fetchEventPlaceholders = (eventTypeId) =>
  axiosInstance.get(`${BASE}/placeholders/event/${eventTypeId}`);

export const fetchCronJobPlaceholders = (cronJobTypeId) =>
  axiosInstance.get(`${BASE}/placeholders/cronjob/${cronJobTypeId}`);

export const fetchEventMappings = (eventId) =>
  axiosInstance.get(`${BASE}/scheduled-events/event-type/${eventId}/mappings`);

export const fetchCronJobMappings = (cronJobId) =>
  axiosInstance.get(
    `${BASE}/scheduled-cron-jobs/cron-job/${cronJobId}/mappings`,
  );
